import { memo, useEffect, useState } from "react";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { BookingStatus, Providers, Status } from "@repo/database";
import { getTextColor } from "@repo/utils";
import { add, differenceInSeconds, format, isSameDay } from "date-fns";
import { MessageCircle, Star } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import type { Booking } from "~/providers/booking-provider";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useSection } from "~/hooks/use-section";
import { useTable } from "~/hooks/use-table";
import { cn } from "~/lib/utils";
import { useBookingManager } from "~/managers/use-booking-manager";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { BookingStatusSelect } from "./booking-status-select";

interface BookingItemProps {
  booking: Booking;
  showTime?: boolean;
  showProvider?: boolean;
  showStatus?: boolean;
  showTable?: boolean;
  className?: string;
  onClick?: () => void;
}

function Provider({ booking }: { booking: Booking }) {
  return (
    <div className="flex items-center">
      {booking.provider === Providers.the_fork && (
        <Tooltip>
          <TooltipTrigger onClick={(e) => e.stopPropagation()}>
            <svg height="20" viewBox="0 0 150 150" width="20">
              <path
                className="fill-bunker-900 dark:fill-gray-200"
                d="M75.876 12.8571C31.1196 12.8571 8.35156 44.3135 8.35156 75.573C8.35156 139.203 79.8718 137.135 79.8718 137.135L78.8672 128.747C78.1892 123.087 74.1295 118.402 68.6428 117.069C58.9414 114.711 44.8954 106.808 44.8954 80.2574C44.8954 62.1931 44.8259 35.7426 44.8259 35.7426H56.1294L62.9579 84.8401C63.5156 88.0193 65.9969 92.4128 72.7036 91.8046L61.996 26.7969H73.1244L85.0122 79.4738C89.0196 79.4738 91.9872 75.7103 91.0913 71.7643L81.3078 32.1362H93.5495L105.756 66.8282C109.348 75.6468 110.108 84.4755 108.489 92.5201C106.959 100.129 103.431 106.436 98.4895 110.886C94.6683 114.328 93.1091 119.661 94.4368 124.655L96.9861 134.245C126.69 125.616 141.648 100.072 141.648 74.7869C141.649 43.9204 119.465 12.8571 75.876 12.8571Z"
              />
            </svg>
          </TooltipTrigger>
          <TooltipContent>The fork</TooltipContent>
        </Tooltip>
      )}

      {booking.provider === Providers.social_deal && (
        <Tooltip>
          <TooltipTrigger onClick={(e) => e.stopPropagation()}>
            <svg height="20" viewBox="0 0 400.000000 400.000000" width="20">
              <path
                className="fill-bunker-900 dark:fill-gray-200"
                d="M166.5 1.1c-29.4 3-53.3 9.4-76 20.4C37.4 47.2 7.9 95.8.9 169.4c-1.7 17.9-.6 61.7 1.9 78.1 9.8 63 37.7 105.7 85 129.8 18.5 9.5 42.1 16.6 67.7 20.4 17.6 2.6 70.6 2.6 89-.1 82.1-11.7 131-54.4 148.8-130.1 5.3-22.2 6.2-32.3 6.2-67.5 0-26-.4-34.9-1.8-44.5-6.8-45.9-22.2-80-47.7-105.5-28-27.9-66.2-43.8-117.9-49-11.9-1.2-53.8-1.1-65.6.1zm91.7 71c21.1 4.4 35.7 25.8 31.9 46.6-1.7 9.1-6.2 17.3-13.2 23.8-8.3 7.6-14.6 9.9-27.4 10-9.2 0-10.5-.3-16.7-3.2-8.8-4.1-16-11.3-20.1-20.1-3-6.2-3.2-7.5-3.2-17.2 0-9.9.2-11 3.3-17.2 8.7-17.7 26.6-26.6 45.4-22.7zm-68.8 2.1c1.4 2 1.6 11.2 1.6 79.9 0 72.7-.1 77.7-1.7 79.1-2.9 2.5-5 2.8-11 1.7-32.4-5.7-58-29.1-66.4-60.5-2.8-10.2-3-29.7-.6-39.6 5-20.3 17.3-38.1 34.1-49.2 12.7-8.5 26.3-13.4 37.3-13.5 4.3-.1 5.5.3 6.7 2.1zm40.6 92.9c26.2 6.9 47.9 27.7 56.6 54.4 3.2 9.7 4.4 29.1 2.5 40.1-3.3 18.7-14.7 37.3-30.6 50-15.2 12.1-42.7 20.3-48.1 14.3-1.8-2-1.9-4.8-1.9-79.6v-77.5l2.8-2.4c3.2-2.7 5.7-2.6 18.7.7zm-66.9 81.5c20.5 6.1 32.8 26.6 28.5 47.4-3.6 17-16.1 29-33.6 32.2-17.2 3.2-35.5-6.5-43.3-22.9-3-6.4-3.2-7.5-3.2-17.8 0-10 .3-11.5 2.8-16.6 9.1-18.6 29.6-28 48.8-22.3z"
              />
            </svg>
          </TooltipTrigger>
          <TooltipContent>Social deal</TooltipContent>
        </Tooltip>
      )}

      {booking.provider === Providers.allindex ||
        (booking.provider === Providers.widget && (
          <Tooltip>
            <TooltipTrigger onClick={(e) => e.stopPropagation()}>
              <svg
                className="w-6"
                version="1.1"
                viewBox="0 0 1256.4 1161.2"
                x="0px"
                y="0px"
              >
                <rect className="hidden" />
                <g>
                  <g>
                    <polygon
                      className="fill-[#36C6F4]"
                      points="526.9,580.6 359.5,580.6 19.2,233 186.7,233 516.3,569.8 		"
                    />
                    <polygon
                      className="fill-[#32B2EF]"
                      points="526.9,580.6 491.6,616.7 325.2,786.6 157.7,786.6 359.3,580.6 		"
                    />
                  </g>
                  <g>
                    <polygon
                      className="fill-[#2063DB]"
                      points="1059.6,396.4 875.9,580.6 706.9,580.6 722.8,564.6 890.7,396.4 		"
                    />
                    <polygon
                      className="fill-[#2577E0]"
                      points="1237.2,942.8 1068.1,942.8 735.6,609.4 706.9,580.6 875.9,580.6 		"
                    />
                  </g>
                  <g>
                    <polygon
                      className="fill-[#5A57E2]"
                      points="616.9,381.2 616.9,550.6 511.2,444.7 99,31.4 268,31.4 		"
                    />
                    <polygon
                      className="fill-[#4554DE]"
                      points="1016.7,149.8 722.5,444.7 616.9,550.6 616.9,381.2 847.7,149.8 		"
                    />
                  </g>
                  <g>
                    <polygon
                      className="fill-[#298AE5]"
                      points="1134.8,1129.8 965.8,1129.8 617,780.2 617,610.7 722.5,716.5 		"
                    />
                    <polygon
                      className="fill-[#2D9EEA]"
                      points="617,610.7 617,780.2 616.9,780 386.1,1011.4 217.1,1011.4 511.3,716.5 616.9,610.6 		"
                    />
                  </g>
                </g>
              </svg>
            </TooltipTrigger>
            <TooltipContent>Booking widget</TooltipContent>
          </Tooltip>
        ))}

      {!booking.provider && (
        <Tooltip>
          <TooltipTrigger onClick={(e) => e.stopPropagation()}>
            <MenuBookIcon fontSize="small" />
          </TooltipTrigger>
          <TooltipContent>Manual</TooltipContent>
        </Tooltip>
      )}

      {booking.provider === Providers.google_reserve && (
        <Tooltip>
          <TooltipTrigger onClick={(e) => e.stopPropagation()}>
            <svg
              height="20"
              viewBox="0 0 186.69 190.5"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g transform="translate(1184.583 765.171)">
                <path
                  clipPath="none"
                  d="M-1089.333-687.239v36.888h51.262c-2.251 11.863-9.006 21.908-19.137 28.662l30.913 23.986c18.011-16.625 28.402-41.044 28.402-70.052 0-6.754-.606-13.249-1.732-19.483z"
                  fill="#4285f4"
                  mask="none"
                />
                <path
                  clipPath="none"
                  d="M-1142.714-651.791l-6.972 5.337-24.679 19.223h0c15.673 31.086 47.796 52.561 85.03 52.561 25.717 0 47.278-8.486 63.038-23.033l-30.913-23.986c-8.486 5.715-19.31 9.179-32.125 9.179-24.765 0-45.806-16.712-53.34-39.226z"
                  fill="#34a853"
                  mask="none"
                />
                <path
                  clipPath="none"
                  d="M-1174.365-712.61c-6.494 12.815-10.217 27.276-10.217 42.689s3.723 29.874 10.217 42.689c0 .086 31.693-24.592 31.693-24.592-1.905-5.715-3.031-11.776-3.031-18.098s1.126-12.383 3.031-18.098z"
                  fill="#fbbc05"
                  mask="none"
                />
                <path
                  clipPath="none"
                  d="M-1089.333-727.244c14.028 0 26.497 4.849 36.455 14.201l27.276-27.276c-16.539-15.413-38.013-24.852-63.731-24.852-37.234 0-69.359 21.388-85.032 52.561l31.692 24.592c7.533-22.514 28.575-39.226 53.34-39.226z"
                  fill="#ea4335"
                  mask="none"
                />
              </g>
            </svg>
          </TooltipTrigger>
          <TooltipContent>Google Reserve</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

function TableInfo({ booking }: { booking: Booking }) {
  const { sections } = useSection();
  const { tables } = useTable();
  const tablesOnBooking = tables.filter((table) =>
    booking.tableIds.includes(table.id),
  );

  if (tablesOnBooking.length === 0) return null;

  return (
    <div className="@md:flex hidden flex-row gap-2 items-center border-r px-2">
      <span className="font-bold">
        {tables
          .filter((table) => booking.tableIds.includes(table.id))
          .map((table) => table.reference)
          .join(", ")}
      </span>
      <span className="font-light text-xs">
        {`(${
          sections.find(
            (section) => section.id === tablesOnBooking[0]?.locationId,
          )?.name
        })`}
      </span>
    </div>
  );
}

function Comments({ booking }: { booking: Booking }) {
  return (
    <>
      {(booking.comment ?? booking.memo) && (
        <div className="flex items-center">
          <MessageCircle />{" "}
        </div>
      )}
    </>
  );
}

function BookingItem({
  booking,
  showStatus,
  showTime,
  className,
  showProvider,
  onClick,
}: BookingItemProps) {
  const { t } = useTranslation("common");

  const { openBookingDialog, updateStatus } = useBookingManager();

  const isMobile = useMediaQuery(768);

  // TODO: configure with personal late threshold variable
  const timeUntilBooking = differenceInSeconds(
    add(booking.startTime, { minutes: 15 }),
    new Date(),
  );

  const [late, setLate] = useState(false);

  const handleOpenBookingDialog = async (
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
    await openBookingDialog(booking);
  };

  const handleBookingStatusUpdate = (status: BookingStatus) => {
    void updateStatus({
      id: booking.id,
      bookingStatus: status,
    });
  };

  useEffect(() => {
    let time = 0;
    if (!isSameDay(booking.startTime, new Date())) return;
    const interval = setInterval(() => {
      time++;
      if (time >= timeUntilBooking) {
        setLate(true);
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [timeUntilBooking, setLate, booking]);

  return (
    <>
      <div
        className={cn(
          "bg-background odd:bg-background-secondary dark:hover:bg-zinc-800 hover:bg-zinc-300 transition-colors relative z-40 flex w-full flex-col border @container",
          (booking.bookingStatus === BookingStatus.canceled ||
            booking.status === Status.trashed) &&
            "disabled",
          booking.bookingStatus === BookingStatus.active &&
            late &&
            "animate-flicker-border border-b-0 border-red-500 last:border-b last:border-red-500 dark:border-red-500 last:dark:border-red-500",
          className,
        )}
        onClick={handleOpenBookingDialog}
        role="button"
      >
        {/* {isBefore(endOfDay(selectedDate), new Date()) && (
          <div className="absolute z-[999] inset-0 bg-gray-900/50 before:absolute before:inset-0 before:bg-[repeating-linear-gradient(45deg,_#ffffff_0px,_#ffffff_2px,_transparent_2px,_transparent_5px)] before:opacity-20 rounded-md pointer-events-none"></div>
        )} */}
        {booking.bookingStatus === BookingStatus.pending &&
          booking.status !== Status.trashed && (
            <div className="absolute inset-0 z-0 animate-pulse bg-yellow-500/50" />
          )}
        <div className="relative z-10 flex w-full h-full py-2">
          {showTime ? (
            <div className="flex min-w-[40px] flex-col gap-1">
              <span>{format(booking.startTime, "p")}</span>
              <span>{format(booking.endTime, "p")}</span>
            </div>
          ) : null}

          <div className="flex flex-1 flex-col @2xl:flex-row border-r @2xl:border-none overflow-hidden">
            <div className="px-2 justify-start border-none @2xl:border-r h-full flex items-center">
              {booking.starred && (
                <Star className="fill-white min-h-4 min-w-4 max-h-4 max-w-4 mr-2" />
              )}

              <Trans
                components={{
                  strong: <span className="text-lg font-medium" />,
                }}
                i18nKey="BookingItem.group_size"
                values={{
                  group_size: booking.groupSize,
                }}
              />
            </div>

            <div className="flex max-w-[200px] flex-1 items-center px-2">
              <span>
                {booking.firstName || booking.lastName
                  ? `${booking.firstName} ${booking.lastName ?? ""}`
                  : t("walk_in")}
              </span>
            </div>

            {!isMobile && (
              <div className="flex flex-col gap-1 justify-center">
                {booking.labels && booking.labels.length > 0 && (
                  <div className="flex items-center">
                    {booking.labels.map((label) => (
                      <div
                        className="rounded px-1 border text-[10px]"
                        style={{
                          backgroundColor: label.color,
                          color: getTextColor(label.color),
                        }}
                        key={label.id}
                      >
                        {label.text}
                      </div>
                    ))}
                  </div>
                )}
                {(booking.comment || booking.memo) && (
                  <div className="flex-1 flex items-center relative overflow-hidden">
                    {booking.comment && (
                      <p className="text-xs text-orange-400 truncate">
                        {`"${booking.comment}"`}
                      </p>
                    )}
                    {booking.memo && (
                      <p className="text-xs text-orange-400 truncate">
                        {`"${booking.memo}"`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex px-2 gap-2">
            <TableInfo booking={booking} />
            {isMobile && <Comments booking={booking} />}
            {showProvider && <Provider booking={booking} />}
          </div>

          {showStatus ? (
            <div className="@sm:flex items-center border-l hidden px-2">
              <BookingStatusSelect
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="w-[130px] h-[40px]"
                onChange={(status) => {
                  void handleBookingStatusUpdate(status);
                }}
                value={booking.bookingStatus}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default memo(BookingItem);
